import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, MapPin, Compass, Camera, Wand2, ArrowLeft } from "lucide-react";
import { TopNav } from "../components/TopNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { generateItinerary } from "../lib/api";
import { MicButton } from "../components/MicButton";
import { toast } from "sonner";

const VIBES = [
  "Old money / luxury",
  "Cyberpunk / neon",
  "Jungle / wellness",
  "Coastal / sunset",
  "Editorial / cinematic",
  "Boho / bohemian",
  "Cozy / cottagecore",
  "Adventure / wild",
  "Foodie / market",
];

const GOALS = [
  { value: "Viral reels", label: "Viral reels" },
  { value: "Instagram photos", label: "Instagram photos" },
  { value: "Hidden gems discovery", label: "Hidden gems" },
  { value: "Food content", label: "Food content" },
  { value: "Romantic trip", label: "Romantic trip" },
  { value: "Solo creator trip", label: "Solo creator trip" },
];

export default function Create() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [prompt, setPrompt] = useState(params.get("q") || "");
  const [destination, setDestination] = useState("");
  const [vibe, setVibe] = useState("");
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const text = prompt.trim();
    if (text.length < 4 && !destination && !vibe && !goal) {
      toast.error("Tell us your vibe — destination, prompt, or vibe at least.");
      return;
    }
    const finalPrompt = text || `${vibe || "Aesthetic"} trip${destination ? ` to ${destination}` : ""}${goal ? ` for ${goal.toLowerCase()}` : ""}`;
    setLoading(true);
    try {
      const itin = await generateItinerary({
        prompt: finalPrompt,
        destination,
        vibe,
        content_goal: goal,
      });
      navigate(`/itinerary/${itin.id}`, { state: { itin } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />
      <main className="pt-28 pb-24 px-5 sm:px-8">
        <div className="mx-auto max-w-3xl">
          <button
            onClick={() => navigate(-1)}
            data-testid="create-back"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-white mb-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>

          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FE0979] mb-2 inline-flex items-center gap-2">
            <Wand2 className="h-3.5 w-3.5" /> Build my vibe trip
          </div>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
            Tell us the<br />
            <span className="text-gradient">vibe</span>. We'll plan the rest.
          </h1>
          <p className="text-zinc-400 mt-3 max-w-xl text-sm sm:text-base">
            One prompt. Or fill in the fields. Or both. We'll find viral spots, captions, photo angles & best shooting times.
          </p>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.2, 0.7, 0.2, 1] }}
            className="mt-10 glass-strong rounded-3xl p-5 sm:p-7 space-y-6"
          >
            <Field label="Your vibe in one sentence" icon={<Sparkles className="h-3.5 w-3.5 text-[#00F2FE]" />}>
              <div className="relative">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={3}
                  placeholder="e.g. Hidden-gem Italy, golden hour cliff cafés, vespa rides, old money editorial reels"
                  data-testid="create-prompt"
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 pr-14 text-base placeholder:text-zinc-500 outline-none focus:border-white/25 transition resize-none font-body"
                />
                <div className="absolute bottom-2.5 right-2.5">
                  <MicButton
                    size="sm"
                    testId="create-prompt-mic-btn"
                    onTranscript={(text) => setPrompt((v) => (v ? `${v.trim()} ${text}` : text))}
                  />
                </div>
              </div>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Destination" icon={<MapPin className="h-3.5 w-3.5 text-[#00F2FE]" />}>
                <input
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="Italy, Tokyo, Bali…"
                  data-testid="create-destination"
                  className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-3 text-sm placeholder:text-zinc-500 outline-none focus:border-white/25 transition font-body"
                />
              </Field>

              <Field label="Travel vibe" icon={<Compass className="h-3.5 w-3.5 text-[#FE0979]" />}>
                <Select value={vibe} onValueChange={setVibe}>
                  <SelectTrigger data-testid="create-vibe" className="bg-white/5 border-white/10 rounded-full h-12 px-4 text-sm font-body hover:bg-white/10 transition">
                    <SelectValue placeholder="Choose a vibe" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                    {VIBES.map((v) => (
                      <SelectItem key={v} value={v} data-testid={`vibe-opt-${v}`}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Content goal" icon={<Camera className="h-3.5 w-3.5 text-[#FE0979]" />}>
              <Select value={goal} onValueChange={setGoal}>
                <SelectTrigger data-testid="create-goal" className="bg-white/5 border-white/10 rounded-full h-12 px-4 text-sm font-body hover:bg-white/10 transition">
                  <SelectValue placeholder="Pick what you'll create" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  {GOALS.map((g) => (
                    <SelectItem key={g.value} value={g.value} data-testid={`goal-opt-${g.value}`}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <button
              type="submit"
              disabled={loading}
              data-testid="create-generate-btn"
              className="w-full inline-flex items-center justify-center gap-2 gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full py-4 hover:opacity-95 active:scale-[0.99] transition disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? "Curating your viral itinerary…" : "Generate My Vibe Trip"}
            </button>
          </motion.form>
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, icon, children }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 mb-2 flex items-center gap-1.5">
      {icon} {label}
    </div>
    {children}
  </div>
);
